import { useCallback, useState } from "react";
import { useHistory } from "react-router";

import { FlightError } from "@b2bportal/air-booking-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  type PaymentError,
  Product,
  type ProductError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import {
  AirChfarExerciseDiscountSelectors,
  CartQuoteErrorModal,
  CartQuoteEventType,
  cartQuoteSelectors,
  CartQuoteState,
  DesktopLayout,
  FlightPassengerEventTypesV2,
  getChildState,
  useCheckoutSend,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
  useFlightErrorConfig,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CallState, CHFAR_ID_QUERY_PARAM } from "@hopper-b2b/types";
import { B2BLoadingPopup, B2BSpinner, WorkingBunnies } from "@hopper-b2b/ui";
import { getEnvVariables, useDeviceTypes } from "@hopper-b2b/utilities";

import { RedirectLoader } from "../../../../components/Body/RedirectLoader";
import { ErrorScreen } from "../../../../components/ErrorScreen";
import { LoadingScreen } from "../../../../components/Slots";
import { useCheckoutMyTripsNavigation } from "../../../../hooks/useCheckoutMyTripsNavigation";
import { useNubankFeatureFlagsContext } from "../../../../utils/featureFlagsContext";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import type { TEvent } from "../../events";
import { getClientAssets } from "../../selectors";
import type { FlightMachineContext } from "../../types";
import { ConnectedPriceBreakdownContent } from "../PriceBreakdownDropdown";
import "./styles.scss";

export const CartQuoteComponent = () => {
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();

  const clientAssets = useSelector(getClientAssets);
  const [state] = useCheckoutState<TEvent, FlightMachineContext>();
  const childState = getChildState(state.value);
  const openCartQuoteModal = useSelector(
    cartQuoteSelectors.getOpenCartQuoteModal
  );

  const [resetLoader, setResetLoader] = useState<boolean>(false);

  const getMessage = (): string => {
    switch (childState) {
      // Schedule happens so fast, the text blinks in and gets replaced by polling
      // case CartQuoteState.schedule:
      //   return t("passengerInformation.validatingPassengers");
      case CartQuoteState.schedule:
      case CartQuoteState.polling:
        return t("savingTraveler");
      default:
        return "";
    }
  };

  const loadingSteps = [
    {
      title: t("pollQuoteLoading.step1Title"),
    },
    {
      title: t("pollQuoteLoading.step2Title"),
    },
    {
      title: t("pollQuoteLoading.step3Title"),
    },
  ];

  const callState = useSelector(cartQuoteSelectors.getCartQuoteCallState);
  const errorState = useSelector(cartQuoteSelectors.getOpenCartQuoteErrorModal);

  if (errorState) {
    return <CartErrorScreen setResetLoader={setResetLoader} />;
  }

  return matchesMobile ? (
    <LoadingScreen
      loading={callState === CallState.InProcess}
      averageLoadingTime={getEnvVariables("pollQuoteLoadingTime") as number}
      className="cart-quote-loading-popup"
      loadingSteps={loadingSteps}
    />
  ) : (
    <>
      <DesktopLayout
        clientLogo={clientAssets?.logo}
        rightContent={<ConnectedPriceBreakdownContent />}
      >
        <B2BSpinner />
      </DesktopLayout>
      <B2BLoadingPopup
        open={openCartQuoteModal}
        message={getMessage()}
        popupSize={"desktop"}
        loadingIcon={<WorkingBunnies />}
        className="schedule-quote-loading"
      />
    </>
  );
};

export const CartErrorScreen = ({
  setResetLoader,
}: {
  setResetLoader: (value: boolean) => void;
}) => {
  return <MappedErrorScreen setResetLoader={setResetLoader} />;
};

/**
 * New error screen using centralized config
 */
const CentralizedMappedErrorScreen = ({
  setResetLoader,
}: {
  setResetLoader: (value: boolean) => void;
}) => {
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartError = useSelector(cartQuoteSelectors.getCartQuoteError);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);

  const navigateToSearch = () => {
    if (chfarId) {
      history.push(
        `/${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`
      );
    } else {
      history.push("/" + PATH_FLIGHTS_SEARCH);
    }
  };
  const navigateToLegacyMyTrips = useCallback(() => {
    history.push("/trips");
  }, [history]);
  const { navigateToMyTrips: navigateFromCheckout, nativeHandoffPending } =
    useCheckoutMyTripsNavigation(navigateToLegacyMyTrips);
  const navigateToMyTrips = useCallback(() => {
    send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
    navigateFromCheckout();
  }, [navigateFromCheckout, send]);

  const { getPurchaseErrorProps, getDefaultProps } = useFlightErrorConfig({
    context: "CART_QUOTE",
    handlers: {
      navigateToFlightSearch: () => {
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
        navigateToSearch();
      },
      navigateToMyTrips,
      navigateToConfirmation: () => {
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
        // CartQuote shouldn't navigate to confirmation, but handler is required
        navigateToSearch();
      },
      openPassengerPicker: () => {
        send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
      },
      goBackToPassengerInfo: () => {
        send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
      },
      openPassportForm: () => {
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
        send(FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM);
      },
      retryQuote: () => {
        setResetLoader(true);
        send(CartQuoteEventType.RETRY_QUOTE);
      },
      clearError: () => {
        send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
      },
    },
  });

  const getErrorModalProps = () => {
    if (
      cartError?.type === CartQuoteErrorModal.FailedPolling &&
      cartError.data?.[0]
    ) {
      return getPurchaseErrorProps(cartError.data[0] as PurchaseError);
    }
    return getDefaultProps();
  };

  const errorModalProps = getErrorModalProps();

  return (
    <>
      {nativeHandoffPending ? <RedirectLoader /> : null}
      <ErrorScreen
        title={errorModalProps.title}
        subtitle={errorModalProps.subtitle}
        primaryButtonText={errorModalProps.primaryButtonText}
        primaryOnClick={errorModalProps.primaryOnClick}
        secondaryButtonText={errorModalProps.secondaryButtonText}
        secondaryOnClick={errorModalProps.secondaryOnClick}
        className={errorModalProps.className ?? "error"}
        onBack={() => {
          send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
          send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
        }}
      />
    </>
  );
};

/**
 * Legacy error screen (original implementation)
 */
const LegacyMappedErrorScreen = ({
  setResetLoader,
}: {
  setResetLoader: (value: boolean) => void;
}) => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartError = useSelector(cartQuoteSelectors.getCartQuoteError);
  const allowedQuoteRetry = useSelector(cartQuoteSelectors.getAllowQuoteRetry);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);

  const clearError = () => send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
  const searchAgain = () => {
    clearError();
    //ChFAR exercise
    if (chfarId)
      history.push(
        `/${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`
      );
    else history.push("/" + PATH_FLIGHTS_SEARCH);
  };
  const updateTravelers = () => {
    send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
    clearError();
  };
  const retryQuote = () => {
    setResetLoader(true);
    send(CartQuoteEventType.RETRY_QUOTE);
  };

  const openPassportForm = () =>
    send(FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM);

  const DEFAULT_ERROR_MODEL_PROPS = {
    title: t("checkoutError.genericErrorTitle"),
    subtitle: t("checkoutError.genericErrorSubtitle"),
    ...(allowedQuoteRetry
      ? {
          primaryButtonText: t("checkoutError.tryAgain"),
          primaryOnClick: retryQuote,
          secondaryButtonText: t("checkoutError.findNewFlight"),
          secondaryOnClick: searchAgain,
        }
      : {
          primaryButtonText: t("checkoutError.findNewFlight"),
          primaryOnClick: searchAgain,
        }),
  };

  const getFlightError = (flightError: FlightError) => {
    switch (flightError) {
      case FlightError.DuplicatedPassport:
        return {
          title: t("checkoutError.duplicatedPassportTitle"),
          subtitle: t("checkoutError.duplicatedPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.DuplicatedNationalId:
        return {
          title: t("checkoutError.duplicatedNationalIdTitle"),
          subtitle: t("checkoutError.duplicatedNationalIdSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.checkInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ExpiringPassport:
        return {
          title: t("checkoutError.expiringPassportTitle"),
          subtitle: t("checkoutError.expiringPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ExpiredPassport:
        return {
          title: t("checkoutError.expiredPassportTitle"),
          subtitle: t("checkoutError.expiredPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantKTN:
        return {
          title: t("checkoutError.illegalLapInfantKTNTitle"),
          subtitle: t("checkoutError.illegalLapInfantKTNSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantRedress:
        return {
          title: t("checkoutError.illegalLapInfantRedressTitle"),
          subtitle: t("checkoutError.illegalLapInfantRedressSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidCustomer:
        return {
          title: t("checkoutError.invalidCustomerTitle"),
          subtitle: t("checkoutError.invalidCustomerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengerName:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerName"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassportNumber:
        return {
          title: t("checkoutError.invalidPassportNumberTitle"),
          subtitle: t("checkoutError.invalidPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassportForm,
        };
      case FlightError.LapInfantTooOld:
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.LapInfantsUnsupported:
        return {
          title: t("checkoutError.lapInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.lapInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedKnownTravelerNumber:
        return {
          title: t("checkoutError.malformedKnownTravelerNumberTitle"),
          subtitle: t("checkoutError.malformedKnownTravelerNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedRedressNumber:
        return {
          title: t("checkoutError.malformedRedressNumberTitle"),
          subtitle: t("checkoutError.malformedRedressNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingAirlineLocator:
        return {
          title: t("checkoutError.missingAirlineLocatorTitle"),
          subtitle: t("checkoutError.missingAirlineLocatorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.MissingNationality:
        return {
          title: t("checkoutError.missingNationalityTitle"),
          subtitle: t("checkoutError.missingNationalitySubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingPassport:
        return {
          title: t("checkoutError.missingPassportTitle"),
          subtitle: t("checkoutError.missingPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassportForm,
        };
      case FlightError.MissingPassportNumber:
        return {
          title: t("checkoutError.missingPassportNumberTitle"),
          subtitle: t("checkoutError.missingPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassportForm,
        };
      case FlightError.MissingPassportOrNationalId:
        return {
          title: t("checkoutError.missingPassportOrNationalIdTitle"),
          subtitle: t("checkoutError.missingPassportOrNationalIdSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassportForm,
          className: "missing",
        };
      case FlightError.NoAvailability:
        return {
          title: t("checkoutError.noAvailabilityTitle"),
          subtitle: t("checkoutError.noAvailabilitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.NoAdultPassenger:
        return {
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoContactInformation:
        return {
          title: t("checkoutError.noContactInformationTitle"),
          subtitle: t("checkoutError.noContactInformationSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoTicketlessResponse:
        return {
          title: t("checkoutError.noTicketlessResponseTitle"),
          subtitle: t("checkoutError.noTicketlessResponseSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.PassengerNameTooShort:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.passengerNameTooShortSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ProviderError:
        return {
          title: t("checkoutError.providerErrorTitle"),
          subtitle: t("checkoutError.providerErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.SeatedInfantsUnsupported:
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TooManyLapInfants:
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TooManyPassengers:
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TransientServiceError:
        return {
          title: t("checkoutError.transientServiceErrorTitle"),
          subtitle: t("checkoutError.transientServiceErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnknownSabreAppError:
        return {
          title: t("checkoutError.unknownSabreAppErrorTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnsupportedNameCharacters:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.unsupportedNameCharactersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.UpgradeRequired:
        return {
          title: t("checkoutError.upgradeRequiredTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      // default FlightErrors that should not occur in CartQuote
      case FlightError.DuplicateBooking:
      case FlightError.ExcessiveActivity:
      case FlightError.ExpiredNationalId:
      case FlightError.ExpiringNationalId:
      case FlightError.ExtraBagsError:
      case FlightError.Inactivity:
      case FlightError.InvalidPassportOrNationalIdNumber:
      case FlightError.MissingNationalId:
      case FlightError.PassengerNotFound:
      case FlightError.PassthroughPaymentError:
      case FlightError.SessionClosed:
      case FlightError.ShopAsyncError:
      case FlightError.TicketingNotStarted:
      case FlightError.TicketingUnconfirmed:
      case FlightError.TicketingUnknown:
      case FlightError.UnexpectedPassengers:
      case FlightError.Unknown:
      case FlightError.UnsupportedPaymentKind:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        // Will throw a compiler error when there are more FlightError's added to the api
        return ensureExhaustive(flightError, DEFAULT_ERROR_MODEL_PROPS);
    }
  };

  const getProductError = (error: ProductError) => {
    switch (error.value.type) {
      case Product.Flight:
      case Product.MultiProviderFlight:
      case Product.Seats: {
        const flightError = error.value.value as FlightError;
        return getFlightError(flightError);
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getPaymentError = (_error: PaymentError) => {
    return DEFAULT_ERROR_MODEL_PROPS;
  };

  const getErrorModalProps = (
    type?: CartQuoteErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartQuoteErrorModal.FailedPolling: {
        const head = data?.[0];
        switch (head?.Error) {
          case PurchaseErrorEnum.NoAvailability:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PurchaseErrorEnum.PaymentError:
            return getPaymentError(head as PaymentError);
          case PurchaseErrorEnum.ProductError:
            return getProductError(head as ProductError);
          default:
            return DEFAULT_ERROR_MODEL_PROPS;
        }
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };
  const errorModalProps = getErrorModalProps(
    cartError?.type,
    cartError?.data as PurchaseError[]
  );

  return <ErrorScreen {...errorModalProps} onBack={updateTravelers} />;
};

/**
 * Error screen component that switches between legacy and centralized config
 * based on feature flag
 */
export const MappedErrorScreen = ({
  setResetLoader,
}: {
  setResetLoader: (value: boolean) => void;
}) => {
  const { enableCentralizedErrorConfig } = useNubankFeatureFlagsContext();

  if (enableCentralizedErrorConfig) {
    return <CentralizedMappedErrorScreen setResetLoader={setResetLoader} />;
  }

  return <LegacyMappedErrorScreen setResetLoader={setResetLoader} />;
};
